<template>
  <div v-if="asset" class="content-with-spacing">
    <div class="heading-container">
      <h1 v-if="asset.name">{{ asset.name }}</h1>
    </div>
    <div class="meeting-container panel panel-default">
        <div class="meeting-content panel-body">
          <h2>Meeting Owner</h2>
          <user-profile-card
            :name="asset.meeting_owner_name"
            :role="asset.support_function"
          ></user-profile-card>
          <p class="meeting-description">{{ asset.description }}</p>
          <hr>
          <div class="meeting-details-cards">
            <div class="meeting-card">
              <div class="card-icon">
                <i class="fa fa-calendar-days"></i>
              </div>
              <div class="card-info">
                <h4 class="card-title">Date</h4>
                <div class="card-content">
                  <div v-if="asset.meeting_start">
                    At: {{ parseTimestamp(asset.meeting_start, undefined, "do MMM yyyy") }}
                  </div>
                  <div v-else>
                    <i>To be confirmed</i>
                  </div>
                </div>
              </div>
            </div>

            <div class="meeting-card">
              <div class="card-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="card-info">
                <h4 class="card-title">Time</h4>
                <div class="card-content">
                  <div v-if="asset.meeting_start && asset.meeting_end">
                    {{ parseTimestamp(asset.meeting_start, undefined, "HH:mm") }} - {{ parseTimestamp(asset.meeting_end, undefined, "HH:mm") }} ({{ Intl.DateTimeFormat().resolvedOptions().timeZone}})
                  </div>
                  <div v-else-if="asset.meeting_start">
                    {{ parseTimestamp(asset.meeting_start, undefined, "HH:mm") }} ({{ Intl.DateTimeFormat().resolvedOptions().timeZone}})
                  </div>
                  <div v-else>
                    <i>To be confirmed</i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="meeting-actions">
            <a :href="asset.meeting_link"
              target="_blank"
              class="btn btn-primary join-meeting-btn"
              :disabled="meetingLinkDisabled"
            >
              <i class="fa-solid fa-external-link-alt"></i>
              Join Meeting
          </a>
          </div>
        </div>
    </div>
    <div v-if="asset.is_progress_review" class="meeting-notes panel panel-default">
      <div class="panel-body">
        <h2>Meeting Notes</h2>
        <hr>
        <empty-placeholder
          v-if="!asset.latest_submission_id"
          title="No meeting notes available"
          info="The meeting notes are not available yet."
        />
        <meeting-form-page v-else :is-read-only="true"/>
      </div>
    </div>
  </div>
</template>

<style scoped>
.heading-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin: 20px auto;
}

.meeting-content h3 {
  text-align: left;
  font-weight: normal;
}

hr {
  border: 1px solid var(--kate-stroke-dark);
  opacity: 0.3;
}

.meeting-content > p {
  text-align: left;
  margin-bottom: 40px;
}

/* Meeting Details Cards */
.meeting-details-cards {
  display: flex;
  gap: 20px;
  margin: 30px 0;
  justify-content: space-between;
}

.meeting-card {
  flex: 1;
}

.meeting-owner-profile-card,
.meeting-card {
  align-items: center;
  display: flex;
  gap: 15px;
  padding: 15px;
  border-radius: 15px;
  background-color: var(--kate-panel-alt);
  border: 1px solid var(--kate-stroke-dark);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
  padding: 10px;
}

.card-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-title {
  margin: 0;
  color: var(--kate-type-primary);
}

.card-content {
  font-size: 1.25em;
  line-height: 1.4;
}

/* Meeting Join Section */
.meeting-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin: 30px 0;
}

.join-meeting-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
}

.progress-review-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.progress-review-meta {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.meta-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid var(--kate-border-light, #eee);
}

.meta-item:last-child {
  border-bottom: none;
}

.meta-label {
  font-weight: 600;
  color: var(--kate-text-secondary, #666);
  font-family: "apercu_monoregular", monospace;
  min-width: 150px;
  text-align: left;
}

.meta-value {
  flex: 1;
  text-align: right;
  color: var(--kate-type-light);
  font-weight: 500;
}

.capitalize-text {
  text-transform: capitalize;
}

/* Profile Card Styles */
.meeting-owner-profile-card {
  margin: 20px 0;
}

.profile-avatar {
  flex-shrink: 0;
}

.avatar-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.profile-name {
  margin: 0;
  font-size: 1.25em;
  line-height: 1.2;
}

.profile-role,
.profile-email {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import MeetingFormPage from '../questionnaires/meetings/meeting-form-page.vue';
import EmptyPlaceholder from '../components/empty-placeholder.vue';
import UserProfileCard from '../components/user-profile-card.vue';

export default {
  components: {
    UserProfileCard,
    MeetingFormPage,
    EmptyPlaceholder,
  },

  mixins: [TimeMixin],

  props: {
    asset: {
      type: Object,
    },
    sidebarReady: {
      type: Boolean,
    },
  },

  mounted() {
    this.$Loading.finish();
  },

  computed: {
    meetingLinkDisabled() {
      return !this.asset.meeting_link ? 'disabled' : undefined;
    },
  },
};
</script>
